import type { LocationQueryRaw } from "vue-router"
import { isEmbeddedContext } from "@common/services/embed-service"
import {
  ENROLLMENT_SUBFLOW_ROUTES,
  type EnrollRouteName,
  type NonVpcBenefitPlanSelection
} from "@/modules/enrollment-new/enrollment-flow-types"
import { getEmployerAllowanceCents } from "@/modules/enrollment-new/enrollment-flow-helpers"

/** Query-param value the composable looks for to short-circuit Back to Sign Forms. */
export const SIGN_FORMS_EDIT_ORIGIN = "sign-forms" as const

export interface EnrollmentRouteLocation {
  name: EnrollRouteName
  params?: Record<string, string>
  query?: LocationQueryRaw
}

/** Where to send the member once a flow is started: the embed skips the enrollment router screen. */
export function enrollmentFlowStartRoute(): { name: "enroll:background" | "enroll-new-start" } {
  return { name: isEmbeddedContext() ? "enroll:background" : "enroll-new-start" }
}

/** Primer completion always continues to marketplace; Medicare users skip the primer. */
export function planShoppingChooseHealthPlanRoute(args: {
  year: number | string
  enrollmentId: string
  employerAllowanceInCents: number
  query?: LocationQueryRaw
}): EnrollmentRouteLocation {
  return {
    name: ENROLLMENT_SUBFLOW_ROUTES.chooseHealthPlan,
    params: { year: String(args.year) },
    query: {
      ...(args.query ?? {}),
      enrollmentId: args.enrollmentId,
      employerAllowanceInCents: args.employerAllowanceInCents
    }
  }
}

export function carrierQuestionsRoute(args: {
  year: number | string
  enrollmentId: string
  employerAllowanceInCents: number
  query?: LocationQueryRaw
}): EnrollmentRouteLocation {
  return {
    ...planShoppingChooseHealthPlanRoute(args),
    name: ENROLLMENT_SUBFLOW_ROUTES.carrierQuestions
  }
}

/** Keys off the user-profile flag: 65+ → Medicare-call page, else → primer. */
export function planShoppingEntryRoute(args: {
  year: number
  medicareEligibleIn: boolean
  enrollmentId: string
  employerAllowanceInCents: number
  query?: LocationQueryRaw
}): EnrollmentRouteLocation {
  return {
    name: args.medicareEligibleIn
      ? ENROLLMENT_SUBFLOW_ROUTES.scheduleMedicareCall
      : ENROLLMENT_SUBFLOW_ROUTES.ichraShopIntro,
    params: { year: String(args.year) },
    query: {
      ...(args.query ?? {}),
      enrollmentId: args.enrollmentId,
      employerAllowanceInCents: args.employerAllowanceInCents
    }
  }
}

/**
 * Edit-from-Sign-Forms route. Keys off the existing selection (not the user-profile
 * flag) so already-shopped users skip the primer.
 */
export function planShoppingEditRoute(args: {
  year: number
  selection: NonVpcBenefitPlanSelection
  enrollmentId: string
}): EnrollmentRouteLocation | undefined {
  const name = subflowRouteForPlanSelection(args.selection)
  if (!name) return undefined
  return {
    name,
    params: { year: String(args.year) },
    query: {
      enrollmentId: args.enrollmentId,
      employerAllowanceInCents: getEmployerAllowanceCents(args.selection)
    }
  }
}

function subflowRouteForPlanSelection(selection: NonVpcBenefitPlanSelection): EnrollRouteName | undefined {
  if (selection?.type === "external") return ENROLLMENT_SUBFLOW_ROUTES.chooseHealthPlan
  if (selection?.type === "medicare") return ENROLLMENT_SUBFLOW_ROUTES.scheduleMedicareCall
  return undefined
}
